import React from "react";
import { Toaster as Sonner } from "sonner";
import type { ToasterProps as SonnerToasterProps } from "sonner";

type ToasterProps = React.ComponentProps<typeof Sonner>;

function resolveTheme(): ToasterProps["theme"] {
  if (typeof document !== "undefined") {
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
  }
  return "system";
}

const Toaster = ({ ...props }: ToasterProps) => {
  const [theme, setTheme] = React.useState<SonnerToasterProps["theme"]>(resolveTheme());
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const target = document.documentElement;
    const obs = new MutationObserver(() => {
      setTheme(target.classList.contains("dark") ? "dark" : "light");
    });
    obs.observe(target, { attributes: true, attributeFilter: ["class"] });
    setTheme(target.classList.contains("dark") ? "dark" : "light");
    return () => obs.disconnect();
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    try {
      mq.addEventListener("change", handler);
    } catch {
      mq.addListener(handler);
    }
    return () => {
      try {
        mq.removeEventListener("change", handler);
      } catch {
        mq.removeListener(handler);
      }
    };
  }, []);

  return (
    <>
      <Sonner
        theme={theme}
        className="clean-sonner group pointer-events-none"
        style={{ zIndex: 2147483647 }}
        expand={false}
        visibleToasts={isMobile ? 2 : 5}
        offset={isMobile ? 14 : 20}
        richColors={false}
        position={isMobile ? "bottom-center" : "bottom-right"}
        toastOptions={{
          duration: 5000,
          closeButton: true,
          unstyled: false,
          classNames: {
            // Toast container
            toast: [
              'group toast w-full',
              isMobile
                ? 'max-w-[min(92vw,22rem)] rounded-md border border-border/55 bg-background/96 px-3 py-2 text-foreground shadow-md'
                : 'rounded-xl border border-border/40 bg-background/95 px-4 py-3 text-foreground shadow-2xl',
              'pointer-events-auto transition-transform transition-opacity duration-180 ease-out',
              'dark:bg-zinc-950/95 dark:text-zinc-50',
            ].join(' '),

            // Title / primary text
            title: [
              isMobile ? 'text-[13px] font-semibold leading-4 text-foreground dark:text-zinc-50' : 'text-sm font-semibold leading-5 text-foreground dark:text-zinc-50',
            ].join(' '),

            // Description / secondary text
            description: [
              isMobile ? 'mt-0.5 text-[11px] leading-4 text-muted-foreground dark:text-zinc-400' : 'mt-1 text-sm leading-5 text-muted-foreground dark:text-zinc-400',
            ].join(' '),

            // Primary action button (e.g., Deshacer) — more prominent on mobile
            actionButton: [
              isMobile ? 'inline-flex min-h-8 items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-semibold' : 'inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-semibold',
              'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 active:scale-95',
            ].join(' '),

            // Secondary action (cancel) — subtle
            cancelButton: [
              isMobile ? 'inline-flex min-h-8 items-center justify-center rounded-full px-2.5 py-1 text-[11px] font-medium' : 'inline-flex items-center justify-center rounded-full px-3 py-1.5 text-xs font-medium',
              'border border-border bg-background text-foreground',
            ].join(' '),

            // Close button (icon)
            closeButton: [
              isMobile ? 'h-7 w-7' : '',
              'rounded-md border border-border bg-background text-muted-foreground',
              'hover:bg-muted hover:text-foreground',
            ].join(' '),
          },
        }}
        {...props}
      />
    </>
  );
};

export { Toaster };
